import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Event, ParticipantDetailResponse } from '../utils/types';
import { apiGet, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ParticipantList: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [participants, setParticipants] = useState<ParticipantDetailResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const { token, user } = useAuth();

    // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  useEffect(() => {
    if (token && eventId) {
      loadEventDetails();
      loadParticipants();
    }
  }, [token, eventId]);

  const loadEventDetails = async () => {
    if (!eventId || !token) return;
    
    try {
      const eventData = await apiGet<Event>(`/events/${eventId}`, token);
      setEvent(eventData);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to load event: ${err.message}`);
      } else {
        setError('Failed to load event details');
      }
    }
  };

  const loadParticipants = async () => {
    if (!eventId || !token) return;
    
    try {
      setLoading(true);
      const participantsData = await apiGet<ParticipantDetailResponse[]>(`/events/${eventId}/participants/all`, token);
      setParticipants(participantsData);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to load participants: ${err.message}`);
      } else {
        setError('Failed to load participants');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportParticipants = async () => {
    if (!eventId || !token) return;
    
    try {
      setExporting(true);
      setError('');
      
      // Create a blob URL and trigger download
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/events/${eventId}/participants/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${event?.name?.replace(/\s+/g, '_') || 'event'}_participants.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (err) {
      if (err instanceof Error) {
        setError(`Export failed: ${err.message}`);
      } else {
        setError('Failed to export participants');
      }
    } finally {
      setExporting(false);
    }
  };

  if (!isOrganizer) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Only organizers can view the participant list.</p>
        <Link to="/dashboard" style={{ color: '#007bff', textDecoration: 'none' }}>
          Return to Dashboard
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading participants...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px',
          borderRadius: '4px',
          border: '1px solid #f5c6cb',
          marginBottom: '20px'
        }}>
          {error}
        </div>
        <Link to="/dashboard" style={{ color: '#007bff', textDecoration: 'none' }}>
          Return to Dashboard
        </Link>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLocationDisplay = (participant: ParticipantDetailResponse) => {
    if (!participant.location) return 'Not specified';
    if (participant.location === 'Other' && participant.location_other) {
      return `Other (${participant.location_other})`;
    }
    return participant.location;
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'available': return '#28a745';
      case 'in_team': return '#007bff';
      case 'unavailable': return '#6c757d';
      default: return '#ffc107';
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <Link to="/dashboard" style={{ color: '#007bff', textDecoration: 'none' }}>
            ← Back to Dashboard
          </Link>
        </div>
        <h1 style={{ margin: '0', color: '#333' }}>
          📋 Participants - {event?.name || 'Event'}
        </h1>
        <p style={{ color: '#666', margin: '5px 0' }}>
          Manage and export participant information for this event
        </p>
      </div>

      {/* Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #dee2e6'
      }}>
        <div>
          <strong>{participants.length}</strong> total participants
        </div>
        <button
          onClick={exportParticipants}
          disabled={exporting || participants.length === 0}
          style={{
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 20px',
            fontSize: '14px',
            cursor: exporting || participants.length === 0 ? 'not-allowed' : 'pointer',
            opacity: exporting || participants.length === 0 ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {exporting ? '⏳ Exporting...' : '📥 Export CSV'}
        </button>
      </div>

      {/* Participants Table */}
      {participants.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>👥</div>
          <h3 style={{ color: '#666', margin: '0 0 10px 0' }}>No Participants Yet</h3>
          <p style={{ color: '#888', margin: '0' }}>
            Participants will appear here once they register for this event.
          </p>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          border: '1px solid #dee2e6',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              minWidth: '800px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Name
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Email
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Location
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Team
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Status
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Skills
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Registered
                  </th>
                </tr>
              </thead>
              <tbody>
                {participants.map((participant, index) => (
                  <tr 
                    key={participant.email}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                    }}
                  >
                    <td style={{
                      padding: '12px',
                      borderBottom: '1px solid #dee2e6',
                      fontWeight: '500'
                    }}>
                      {participant.display_name}
                    </td>
                    <td style={{
                      padding: '12px',
                      borderBottom: '1px solid #dee2e6',
                      color: '#666'
                    }}>
                      {participant.email}
                    </td>
                    <td style={{
                      padding: '12px',
                      borderBottom: '1px solid #dee2e6'
                    }}>
                      {getLocationDisplay(participant)}
                    </td>
                    <td style={{
                      padding: '12px',
                      borderBottom: '1px solid #dee2e6'
                    }}>
                      {participant.team_assignment ? (
                        <Link 
                          to={`/teams/${participant.team_assignment.team_id}`}
                          style={{
                            color: '#007bff',
                            textDecoration: 'none',
                            fontWeight: '500'
                          }}
                        >
                          {participant.team_assignment.team_name}
                        </Link>
                      ) : (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                          No team
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding: '12px',
                      borderBottom: '1px solid #dee2e6'
                    }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: getStatusBadgeColor(participant.availability_status),
                        color: 'white',
                        textTransform: 'capitalize'
                      }}>
                        {participant.availability_status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px',
                      borderBottom: '1px solid #dee2e6',
                      maxWidth: '200px'
                    }}>
                      {participant.skills.length > 0 ? (
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '4px'
                        }}>
                          {participant.skills.slice(0, 3).map((skill) => (
                            <span
                              key={skill}
                              style={{
                                display: 'inline-block',
                                padding: '2px 6px',
                                backgroundColor: '#e9ecef',
                                color: '#495057',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}
                            >
                              {skill}
                            </span>
                          ))}
                          {participant.skills.length > 3 && (
                            <span style={{
                              color: '#6c757d',
                              fontSize: '11px',
                              alignSelf: 'center'
                            }}>
                              +{participant.skills.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                          No skills listed
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding: '12px',
                      borderBottom: '1px solid #dee2e6',
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      {formatDate(participant.registered_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantList;
